import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Collections from "./Collections";
import CollectionService from "@/services/CollectionService";
import { Button } from "@/components/ui/button";
import CreateCollectionDialog from "@/components/Collections/CreateCollectionDialog";
import InnerContainerLoader from "@/components/InnerContainerLoader";

const CollectionsHome = () => {
  const navigate = useNavigate();
  const [collections, setCollections] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const createCollection = async (newTitle) => {
    try {
      const response = await CollectionService.createCollection("en", newTitle);
      navigate(`/collections/${response.id}/edit`);
    } catch (error) {
      console.error("Failed to create new collection:", error);
    }
  };

  const fetchCollections = useCallback(async () => {
    try {
      const response = await CollectionService.getUserCollections();
      setCollections(response);
    } catch (error) {
      console.error("Failed to fetch collections:", error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCollections();
  }, [fetchCollections]);

  const handleCollectionClick = (collectionId) => {
    navigate(`/collections/${collectionId}/edit`);
  };

  const openDialog = () => {
    setIsDialogOpen(true);
  };

  const closeDialog = () => {
    setIsDialogOpen(false);
  };

  const collectionSummaryLabel = isLoading
    ? "Curating your collections..."
    : collections.length === 1
    ? "1 saved collection"
    : `${collections.length} saved collections`;

  return (
    <div
      className="bg-grid relative isolate flex min-h-screen flex-col overflow-hidden text-fg"
    >
      <div className="relative mx-auto flex w-full max-w-6xl flex-1 flex-col px-4 pb-20 pt-20 sm:px-8 lg:px-12 xl:px-16 md:pt-24">
        <header className="flex flex-col gap-8 md:flex-row md:items-end md:justify-between">
          <div className="flex max-w-2xl flex-col gap-4">
            <span className="inline-flex w-fit items-center gap-2 rounded-full border border-line bg-surface/80 px-4 py-1 text-[10px] uppercase tracking-[0.4em] text-primary/80">
              Your Space
            </span>
            <div className="space-y-4">
              <h1 className="text-4xl font-semibold tracking-tight text-fg sm:text-5xl">
                Collections
              </h1>
              <p className="text-sm text-fg-muted sm:text-base">
                Curate the material that inspires you and pick up exactly where you left off.
                Everything stays in sync so you can focus on practicing.
              </p>
            </div>
            <div className="text-xs text-fg-muted sm:text-sm">
              {collectionSummaryLabel}
            </div>
          </div>
          <Button
            size="lg"
            className="group relative self-start overflow-hidden rounded-full border border-line bg-elevated px-6 py-5 text-sm font-semibold text-fg shadow-panel transition-all duration-200 ease-out hover:border-primary/40 hover:bg-elevated hover:text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/60 focus-visible:ring-offset-2 focus-visible:ring-offset-canvas md:self-auto md:px-8"
            onClick={openDialog}
          >
            <span className="absolute inset-0 bg-gradient-to-r from-primary via-primary to-primary opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
            <span className="relative flex items-center gap-2 text-base">
              <span className="text-xl leading-none">+</span>
              New Collection
            </span>
          </Button>
        </header>

        <section className="mt-12 flex-1">
          <div className="relative h-full overflow-hidden rounded-3xl border border-line bg-surface/80 p-5 shadow-panel backdrop-blur-xl transition-all duration-300 ease-out sm:p-8">
            <div className="pointer-events-none absolute inset-x-12 top-0 h-px bg-gradient-to-r from-transparent via-fg/40 to-transparent" />
            {isLoading ? (
              <div className="flex min-h-[240px] items-center justify-center">
                <InnerContainerLoader offset={32} />
              </div>
            ) : (
              <Collections
                collections={collections}
                onCollectionClick={handleCollectionClick}
                onCollectionsChange={setCollections}
              />
            )}
          </div>
        </section>
      </div>
      <CreateCollectionDialog
        open={isDialogOpen}
        onClose={closeDialog}
        onCreateCollection={createCollection}
        onCollectionCreated={fetchCollections}
      />
    </div>
  );
};

export default CollectionsHome;
